import { useEffect, useRef, useState } from "react";
import { SearchBoxStyled, SearchIconBtn, SearchField } from "../styled/Header.styled";

/* Parent (bkvenergy.com) header search. Mirrored from its own author CSS:

     form.search-form   position absolute; right 0; top 50%; translateY(-50%); transition width .3s
     .search-field      border 0; 40x40; radius 31px; opacity 0; position relative; z-index 2; cursor pointer
     .search-field.search-input-opened
                        padding 14px 45px 15px 26px; bg #fff; border 1px solid #17b49d; width 424px; opacity 1
     input[type=submit] position absolute; right 5px; top 0; bottom 0; 40x40; z-index 5; icon as bg-image

   Two details that make it read like the parent rather than approximate it:
   - the field is never unmounted; closed it is 40px at opacity 0, so the 0.3s width
     transition has something to animate from (mounting a fresh node just snaps);
   - the submit is anchored inside the field (the field is position:relative), which is
     why the field reserves 45px of right padding when open - the glyph sits inside the pill.

   Parent form internals: GET action https://bkvenergy.com/, field name="s"
   (type=search, minlength=3, required), plus a hidden empty `post_type` - without it
   the query does not resolve to BKV's own results page. */
const SEARCH_ACTION = "https://bkvenergy.com/";

/* `wide` = parent's <=992px case: the field is permanently open inside the menu */
const SearchBox = ({ wide = false }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);
  const boxRef = useRef(null);

  const isOpen = open || wide;

  const openNow = () => {
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const submit = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    /* mirrors the parent's GET query string exactly: ?s=<term>&post_type= */
    window.location.href = `${SEARCH_ACTION}?s=${encodeURIComponent(query.trim())}&post_type=`;
  };

  /* desktop: first click reveals the field, later clicks fall through to the form's
     onSubmit so the search actually runs. The drawer is always open, so it submits. */
  const onIconClick = (e) => {
    if (wide || open) return;
    e.preventDefault();
    openNow();
  };

  /* desktop close affordances: clicking/tapping away collapses the field again */
  useEffect(() => {
    if (!open || wide) return undefined;
    const onPointerDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
    };
  }, [open, wide]);

  return (
    <SearchBoxStyled ref={boxRef} $open={isOpen} $wide={wide} onSubmit={submit} role="search">
      <SearchField
        ref={inputRef}
        $open={isOpen}
        $wide={wide}
        type="search"
        name="s"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onClick={!isOpen ? openNow : undefined}
        placeholder="SEARCH"
        aria-label="Search"
        title="Search for:"
        /* parent's field declares these; mirrored so typing behaves the same */
        minLength={3}
        required
        onKeyDown={(e) => e.key === "Escape" && !wide && setOpen(false)}
      />
      <SearchIconBtn
        type="submit"
        aria-label="Search"
        aria-expanded={isOpen}
        title="Search"
        $open={isOpen}
        onClick={onIconClick}
      />
    </SearchBoxStyled>
  );
};

export default SearchBox;
